import { useEffect, useRef, useState, useCallback } from "react";
import { useMutation, useQuery, useSubscription } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { HASURA_CLAIMS_NAMESPACE, USER_ID_CLAIM, UserClaims } from "common/models/UserClaims";
import { ATMTS_REQUEST_ORIGIN_HEADER_NAME, ATMTS_REQUEST_ORIGIN_HEADER_VALUE } from "common/constants";
import { temporaryToken } from "@app.automotus.io/components/hooks";
import {
  INSERT_USER_APPLICATION_SESSION_EVENT,
  InsertUserApplicationSessionEventData,
  InsertUserApplicationSessionEventVars,
} from "common/graphql/mutations/InsertUserApplicationSessionEvent";
import {
  GET_RECENT_USER_APPLICATION_SESSION_EVENTS,
  GetRecentUserApplicationSessionEventsData,
  GetRecentUserApplicationSessionEventsVars,
} from "common/graphql/queries/GetRecentUserApplicationSessionEvents";
import {
  STREAM_USER_APPLICATION_SESSION_EVENTS,
  StreamUserApplicationSessionEventsData,
  StreamUserApplicationSessionEventsVars,
} from "common/graphql/subscriptions/StreamUserApplicationSessionEvents";
import { UseSessionManagerOptions, UseSessionManagerReturn, SessionData, UserApplicationSessionEvent } from "./types";

/**
 * React hook for managing user application session events to prevent simultaneous editing conflicts.
 *
 * This hook provides real-time session management with conflict detection using a "first-come, first-serve"
 * editing permissions model. It tracks active users on a specific item/resource and determines which user
 * has editing priority based on session start times.
 *
 * @param itemId - The unique identifier of the item/resource being accessed
 * @param options - Configuration options for session behavior
 *
 * @returns Session state and conflict detection data
 *
 * @example
 * ```typescript
 * const {
 *   activeSessions,
 *   activeUserCount,
 *   isCurrentUserOldest
 * } = useSessionManager('item-uuid-123', {
 *   sessionTimeout: 120000, // 2 minutes
 *   heartbeatInterval: 45000 // 45 seconds
 * });
 *
 * if (!isCurrentUserOldest) {
 *   // Show read-only mode with message about other active users
 *   return <ReadOnlyView activeUserCount={activeUserCount} />;
 * }
 * ```
 *
 * ## Key Features:
 *
 * - **Automatic Application Detection**: Detects 'curbsuite' or 'curbpass' from REACT_APP_APP_NAME
 * - **Session Lifecycle Management**: Handles join, heartbeat, and leave events automatically
 * - **Real-time Updates**: Uses GraphQL subscriptions for live session tracking
 * - **Conflict Detection**: Determines editing priority based on oldest session
 * - **Automatic Cleanup**: Removes stale sessions and marks idle users
 * - **Robust Error Handling**: Gracefully handles network failures and edge cases
 * - **Memory Management**: Properly cleans up intervals and subscriptions
 *
 * ## Session Logic:
 *
 * - Users are "active" if their last heartbeat was within sessionTimeout
 * - Users are "idle" if their last heartbeat exceeds 1.5x heartbeatInterval but is still within sessionTimeout
 * - The user with the earliest join timestamp gets editing priority (isCurrentUserOldest = true)
 * - Other users see isEditingDisabled = true with appropriate messaging
 * - Sessions are automatically cleaned up every 10 seconds by default
 *
 * ## Browser Compatibility:
 *
 * - Uses fetch with keepalive for page unload events (modern browsers)
 * - Falls back to navigator.sendBeacon for older browsers
 * - Handles authentication tokens for both authenticated and anonymous users
 */
export function useSessionManager(
  itemId: string | undefined,
  options: UseSessionManagerOptions = {},
): UseSessionManagerReturn {
  const {
    sessionTimeoutSeconds: sessionTimeout = 90 * 1000, // 90 seconds
    heartbeatIntervalSeconds: heartbeatInterval = 30 * 1000, // 30 seconds
    initialFetchIntervalSeconds: initialFetchInterval = 5 * 60 * 1000, // 5 minutes
    staleSessionCleanupIntervalSeconds: staleSessionCleanupInterval = 10 * 1000, // 10 seconds
  } = options;

  const { user: userClaims, getAccessTokenSilently, isAuthenticated } = useAuth0<UserClaims>();
  const currentUserProfileId = (userClaims && userClaims[HASURA_CLAIMS_NAMESPACE][USER_ID_CLAIM]) || "";

  // Determine application from environment
  const application: "curbsuite" | "curbpass" = process.env.REACT_APP_APP_NAME?.includes("curbsuite")
    ? "curbsuite"
    : "curbpass";

  const [activeSessions, setActiveSessions] = useState<Map<string, SessionData>>(new Map());
  const [isCurrentUserOldest, setIsCurrentUserOldest] = useState(false);
  const [editingUserFullName, setEditingUserFullName] = useState<string | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionStartTime = useRef<Date>(new Date());
  const [isInitialLoadInProgress, setIsInitialLoadInProgress] = useState(true);
  const [currentUserSessionConfirmed, setCurrentUserSessionConfirmed] = useState(false);
  const [mountTime] = useState(new Date());

  const [insertEvent] = useMutation<InsertUserApplicationSessionEventData, InsertUserApplicationSessionEventVars>(
    INSERT_USER_APPLICATION_SESSION_EVENT,
  );

  // Initialize session on mount
  useEffect(() => {
    const initializeSession = async () => {
      sessionStartTime.current = new Date();

      if (!currentUserProfileId || !itemId) {
        return;
      }

      try {
        const result = await insertEvent({
          variables: {
            application,
            itemId,
            eventType: "join",
          },
        });

        // Update session start time based on server response if available
        if (result.data?.insertUserApplicationSessionEvent?.createdAt) {
          sessionStartTime.current = new Date(result.data.insertUserApplicationSessionEvent.createdAt);
        }

        setCurrentUserSessionConfirmed(true);
      } catch (error) {
        console.error("Failed to initialize session:", error);
      }
    };

    initializeSession();
  }, [itemId, currentUserProfileId, application, insertEvent]);

  // Send heartbeat events
  useEffect(() => {
    if (!currentUserProfileId || !itemId) {
      return;
    }

    const sendHeartbeat = async () => {
      try {
        await insertEvent({
          variables: {
            application,
            itemId,
            eventType: "heartbeat",
          },
        });
      } catch (error) {
        console.error("Failed to send heartbeat:", error);
      }
    };

    // Start heartbeat
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, heartbeatInterval);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [currentUserProfileId, application, itemId, heartbeatInterval, insertEvent]);

  // Check if current user has oldest session
  const checkOldestSession = useCallback(() => {
    // Don't make decisions until we've confirmed our session with the server
    if (!currentUserSessionConfirmed || isInitialLoadInProgress) {
      return;
    }

    // Add safety check for empty sessions
    if (activeSessions.size === 0 && currentUserProfileId) {
      setIsCurrentUserOldest(true);
      setEditingUserFullName(null);
      return;
    }

    // Don't assume current user is oldest without verification
    if (!currentUserProfileId) {
      setIsCurrentUserOldest(false);
      setEditingUserFullName(null);
      return;
    }

    // Include current user's session in the comparison, but use a more conservative approach
    const allSessions = new Map(activeSessions);
    if (sessionStartTime.current && currentUserProfileId) {
      allSessions.set(currentUserProfileId, {
        userProfileId: currentUserProfileId,
        fullName: null,
        joinedAt: sessionStartTime.current,
        lastSeen: new Date(),
        status: "active",
      });
    }

    // Find the oldest session - use more robust comparison
    let oldestSession: SessionData | null = null;
    let oldestTime = Infinity;

    for (const session of Array.from(allSessions.values())) {
      const sessionTime = session.joinedAt.getTime();
      if (sessionTime < oldestTime || (sessionTime === oldestTime && session.userProfileId < currentUserProfileId)) {
        // Add secondary sort by userProfileId for tie-breaking
        oldestTime = sessionTime;
        oldestSession = { ...session };
      }
    }

    // Add additional safety check
    const isOldest = oldestSession?.userProfileId === currentUserProfileId;

    // Add confidence check - if there are multiple sessions with the same timestamp, be more conservative
    const sessionsAtSameTime = Array.from(allSessions.values()).filter(
      (s) => Math.abs(s.joinedAt.getTime() - oldestTime) < 1000, // Within 1 second
    );

    if (sessionsAtSameTime.length > 1 && isOldest) {
      // If there are conflicts, defer to server-side authority or use a deterministic tiebreaker
      console.warn("Session timestamp conflict detected, using conservative approach");
    }

    setIsCurrentUserOldest(isOldest);
    setEditingUserFullName(isOldest ? null : oldestSession?.fullName ?? null);
  }, [activeSessions, currentUserProfileId, currentUserSessionConfirmed, isInitialLoadInProgress]);

  // Process session events
  const processEvent = useCallback(
    (event: UserApplicationSessionEvent) => {
      const { userProfileId, eventType, createdAt } = event;
      const fullName = event.userFullName;

      // Skip our own events
      if (userProfileId === currentUserProfileId) {
        return;
      }

      setActiveSessions((prev) => {
        const updated = new Map(prev);
        const eventTime = new Date(createdAt);

        switch (eventType) {
          case "join":
            updated.set(userProfileId, {
              userProfileId: userProfileId,
              fullName,
              lastSeen: eventTime,
              joinedAt: eventTime,
              status: "active",
            });
            break;

          case "heartbeat":
            if (updated.has(userProfileId)) {
              const updatedSessionData = updated.get(userProfileId);
              if (updatedSessionData) {
                updated.set(userProfileId, {
                  ...updatedSessionData,
                  fullName: fullName ?? updatedSessionData.fullName,
                  lastSeen: eventTime,
                  status: "active",
                });
              }
            } else {
              // User joined before we started listening
              updated.set(userProfileId, {
                userProfileId: userProfileId,
                fullName,
                lastSeen: eventTime,
                joinedAt: eventTime, // Best guess
                status: "active",
              });
            }
            break;

          case "leave":
            updated.delete(userProfileId);
            break;
        }

        return updated;
      });
    },
    [currentUserProfileId],
  );

  // Stream session events
  useSubscription<StreamUserApplicationSessionEventsData, StreamUserApplicationSessionEventsVars>(
    STREAM_USER_APPLICATION_SESSION_EVENTS,
    {
      variables: {
        application,
        itemId: itemId || "",
        since: new Date(mountTime.getTime() - initialFetchInterval).toISOString(),
      },
      onData: ({ data }) => {
        if (data.data?.userApplicationSessionEventStream) {
          data.data.userApplicationSessionEventStream.forEach(processEvent);
        }
      },
      skip: !itemId,
    },
  );

  // Load initial session state
  useQuery<GetRecentUserApplicationSessionEventsData, GetRecentUserApplicationSessionEventsVars>(
    GET_RECENT_USER_APPLICATION_SESSION_EVENTS,
    {
      variables: {
        application,
        itemId: itemId || "",
        since: new Date(mountTime.getTime() - initialFetchInterval).toISOString(),
      },
      skip: !itemId,
      onCompleted: (data) => {
        if (data?.userApplicationSessionEvents) {
          // Process events to build initial state
          const eventsByUser = new Map<string, UserApplicationSessionEvent[]>();

          data.userApplicationSessionEvents.forEach((event) => {
            const userId = event.userProfileId;
            if (!eventsByUser.has(userId)) {
              eventsByUser.set(userId, []);
            }
            eventsByUser.get(userId)?.push(event);
          });

          // Determine current state for each user
          eventsByUser.forEach((events, userId) => {
            if (userId === currentUserProfileId) {
              return;
            }

            // Sort events by time
            events.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

            const latestEvent = events[events.length - 1];
            const eventTime = new Date(latestEvent.createdAt);
            const isRecent = Date.now() - eventTime.getTime() < sessionTimeout;

            if (isRecent && latestEvent.eventType !== "leave") {
              processEvent(latestEvent);
            }
          });
        }
        setIsInitialLoadInProgress(false);
      },
      onError: (error) => {
        console.error("Failed to load initial session state:", error);
        setIsInitialLoadInProgress(false);
      },
    },
  );

  // Clean up stale sessions and check oldest status
  useEffect(() => {
    const cleanupStale = setInterval(() => {
      const now = Date.now();
      setActiveSessions((prev) => {
        const updated = new Map<string, SessionData>();
        prev.forEach((session, userId) => {
          const timeSinceLastSeen = now - session.lastSeen.getTime();
          if (timeSinceLastSeen < sessionTimeout) {
            updated.set(userId, {
              ...session,
              status: timeSinceLastSeen > heartbeatInterval * 1.5 ? "idle" : "active",
            });
          }
        });
        return updated;
      });
    }, staleSessionCleanupInterval);

    return () => clearInterval(cleanupStale);
  }, [staleSessionCleanupInterval, sessionTimeout, heartbeatInterval]);

  // Update oldest session check when active sessions change
  useEffect(() => {
    checkOldestSession();
  }, [checkOldestSession]);

  // Send leave event on unmount
  useEffect(() => {
    const sendLeaveEvent = async () => {
      if (!currentUserProfileId || !itemId) {
        return;
      }

      try {
        await insertEvent({
          variables: {
            application,
            itemId,
            eventType: "leave",
          },
        });
      } catch (error) {
        console.error("Failed to send leave event:", error);
      }
    };

    // Handle page unload
    const handleBeforeUnload = async () => {
      // Use sendBeacon for synchronous request on page unload
      if ("sendBeacon" in navigator && currentUserProfileId) {
        const uri = process.env.REACT_APP_GRAPHQL_ENDPOINT;
        if (!uri) {
          return;
        }

        let token: string | undefined;
        try {
          if (isAuthenticated) {
            token = await getAccessTokenSilently();
          } else {
            token = temporaryToken();
          }
        } catch (error) {
          console.error("Failed to get token for leave event:", error);
          return;
        }

        const mutation = `
          mutation InsertUserApplicationSessionEvent($application: user_application_enum!, $itemId: uuid!, $eventType: user_application_session_event_type_enum!) {
            insertUserApplicationSessionEvent: insert_user_application_session_event_one(
              object: { application: $application, item_id: $itemId, event_type: $eventType }
            ) {
              id
            }
          }
        `;

        const payload = JSON.stringify({
          query: mutation,
          variables: {
            application,
            itemId,
            eventType: "leave",
          },
        });

        // Create a blob with the proper headers
        const blob = new Blob([payload], {
          type: "application/json",
        });

        // Create form data to include headers
        const formData = new FormData();
        formData.append("operations", payload);

        // Use fetch with keepalive as fallback if sendBeacon doesn't support custom headers
        try {
          fetch(uri, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              [ATMTS_REQUEST_ORIGIN_HEADER_NAME]: ATMTS_REQUEST_ORIGIN_HEADER_VALUE,
              ...(token && { Authorization: isAuthenticated ? `Bearer ${token}` : token }),
            },
            body: payload,
            keepalive: true,
          }).catch(() => {
            // Ignore errors during page unload
          });
        } catch (error) {
          // Fallback to sendBeacon without custom headers as last resort
          navigator.sendBeacon(uri, blob);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      sendLeaveEvent();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [getAccessTokenSilently, isAuthenticated, currentUserProfileId, application, itemId, insertEvent]);

  return {
    activeSessions: Array.from(activeSessions.values()),
    activeUserCount: activeSessions.size,
    isCurrentUserOldest,
    editingUserFullName,
    application,
    isInitialLoadInProgress,
    currentUserSessionConfirmed,
  };
}
